/**
 * Builds the account list for account capture: one shell per selected funding-source type (matched to
 * an existing source of that type when there is one), then every other existing self-funding source,
 * plus the existing-LTC-policy shell. Tracks which shell is expanded and whether the list is complete.
 *
 * The host supplies the selected types and the funding sources (live, or a guided snapshot), so the
 * hook knows nothing about where either is stored.
 */
import { useCallback, useMemo, useState } from 'react';

import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { getPolicyName } from '@shared';
import { ASSET_TYPES, ASSET_TYPE_TO_ACCOUNT_TYPE, type AssetTypeId } from '../config/funding-source-types';
import { isSelfFundingAccountComplete } from '../lib/account-completeness';

export interface AccountShell {
  assetTypeId: AssetTypeId;
  label: string;
  accountType: string;
  fundingSourceId: string | null;
  selfFundingSource: SelfFundingSource | undefined;
  isExistingLtcPolicy?: boolean;
  policyFundingSourceId?: string | null;
  // When isExistingLtcPolicy, policy face amount
  policyMaximumBenefitAmount?: number | null;
  // When isExistingLtcPolicy and policy exists show name
  policyTypeName?: string | null;
}

function isExistingLtcPolicyShellComplete(ltcPolicyIds: string[]): boolean {
  return ltcPolicyIds.length > 0;
}

/** Unique key for each shell so multiple self-funding sources of the same type expand independently. */
export function getShellExpandKey(shell: AccountShell): string {
  if (shell.isExistingLtcPolicy) return 'existing-ltc-policy';
  return shell.fundingSourceId ?? shell.assetTypeId;
}

interface UseAccountAccordionOptions {
  selectedTypes: readonly AssetTypeId[];
  fundingSources: MultipleFundingSources | undefined;
  /** Funding source IDs shared by the partner — skip these during Phase 1 matching so they appear as read-only orphans in Phase 2. */
  partnerSharedFundingSourceIds?: Set<string>;
}

export function useAccountAccordion({
  selectedTypes,
  fundingSources,
  partnerSharedFundingSourceIds,
}: UseAccountAccordionOptions) {
  const { flags } = useFeatureFlags();

  const selectedAssets = selectedTypes;
  const selfFunding = fundingSources?.selfFunding ?? {};
  const ltcPolicy = fundingSources?.ltcPolicy ?? {};
  const ltcPolicyIds = Object.keys(ltcPolicy);
  const firstLtcPolicyId = ltcPolicyIds[0] ?? null;

  // Build account shells: map selected assets to existing funding sources
  // AND include all existing self-funding sources even if not in selected assets
  const accountShells = useMemo((): AccountShell[] => {
    const processedFundingSourceIds = new Set<string>();
    const shells: AccountShell[] = [];

    // First, add all selected assets
    selectedAssets.forEach((assetTypeId: AssetTypeId) => {
      const accountType = ASSET_TYPE_TO_ACCOUNT_TYPE[assetTypeId];
      const label = ASSET_TYPES.find(a => a.id === assetTypeId)?.label ?? assetTypeId;

      if (assetTypeId === 'existing-ltc-policy') {
        if (!flags.existingLtcPolicyTypeOnCard) return;
        const firstPolicy = firstLtcPolicyId
          ? (ltcPolicy[firstLtcPolicyId] as PolicyFundingSource | undefined)
          : undefined;
        const hasCustomName =
          firstPolicy?.policyCustomName != null &&
          String(firstPolicy.policyCustomName).trim().length > 0;
        const policyTypeName =
          hasCustomName || firstPolicy?.policyType != null ? getPolicyName(firstPolicy) : null;
        shells.push({
          assetTypeId: 'existing-ltc-policy',
          label,
          accountType,
          fundingSourceId: null,
          selfFundingSource: undefined,
          isExistingLtcPolicy: true,
          policyFundingSourceId: firstLtcPolicyId,
          policyMaximumBenefitAmount: firstPolicy?.policyMaximumBenefitAmount ?? null,
          policyTypeName,
        });
        return;
      }

      // Find existing funding source that matches this account type.
      // Skip partner-shared sources so they only appear as read-only orphans in Phase 2.
      const matchingEntry = Object.entries(selfFunding).find(
        ([id, source]) =>
          !partnerSharedFundingSourceIds?.has(id) &&
          (source.accountType === accountType || source.selfFundingName === label),
      );

      if (matchingEntry) {
        const [fundingSourceId, source] = matchingEntry;
        processedFundingSourceIds.add(fundingSourceId);
        shells.push({
          assetTypeId,
          label,
          accountType,
          fundingSourceId,
          selfFundingSource: source as SelfFundingSource,
        });
      } else {
        shells.push({
          assetTypeId,
          label,
          accountType,
          fundingSourceId: null,
          selfFundingSource: undefined,
        });
      }
    });

    // Then, add any self-funding sources that weren't matched to selected assets (skip existingLtcPolicy)
    Object.entries(selfFunding).forEach(([fundingSourceId, source]) => {
      if (processedFundingSourceIds.has(fundingSourceId)) {
        return;
      }
      if (source.accountType === 'existingLtcPolicy') {
        return;
      }

      // Reverse-map accountType to assetTypeId
      const accountType = source.accountType ?? '';
      const assetTypeId = (Object.keys(ASSET_TYPE_TO_ACCOUNT_TYPE) as AssetTypeId[]).find(
        key => ASSET_TYPE_TO_ACCOUNT_TYPE[key] === accountType,
      );

      const label =
        source.selfFundingName ??
        (assetTypeId ? ASSET_TYPES.find(a => a.id === assetTypeId)?.label : undefined) ??
        (accountType || 'Unknown Account');

      shells.push({
        assetTypeId: assetTypeId ?? (accountType as AssetTypeId),
        label,
        accountType: accountType || 'unknown',
        fundingSourceId,
        selfFundingSource: source as SelfFundingSource,
      });
    });

    return shells;
  }, [
    selectedAssets,
    selfFunding,
    ltcPolicy,
    firstLtcPolicyId,
    flags.existingLtcPolicyTypeOnCard,
    partnerSharedFundingSourceIds,
  ]);

  // Calculate completion status (keyed by shell expand key for unique identity)
  const completionStatus = useMemo(() => {
    return accountShells.map(shell => ({
      shellKey: getShellExpandKey(shell),
      isComplete: shell.isExistingLtcPolicy
        ? isExistingLtcPolicyShellComplete(ltcPolicyIds)
        : shell.selfFundingSource
          ? isSelfFundingAccountComplete(shell.selfFundingSource)
          : false,
    }));
  }, [accountShells, ltcPolicyIds]);

  const allComplete = completionStatus.every(status => status.isComplete);

  // Find first incomplete account for auto-expand (by shell key)
  const autoExpandedShellKey = useMemo(() => {
    const firstIncomplete = completionStatus.find(status => !status.isComplete);
    return firstIncomplete?.shellKey ?? null;
  }, [completionStatus]);

  const [manuallyExpandedShellKey, setManuallyExpandedShellKey] = useState<string | null>(null);

  const expandedShellKey = manuallyExpandedShellKey ?? autoExpandedShellKey;

  const handleToggle = useCallback(
    (shellKey: string) => {
      setManuallyExpandedShellKey(expandedShellKey === shellKey ? null : shellKey);
    },
    [expandedShellKey],
  );

  const getNextIncompleteShellKey = useCallback(
    (currentShellKey: string): string | null => {
      const currentIndex = accountShells.findIndex(
        shell => getShellExpandKey(shell) === currentShellKey,
      );
      for (let i = currentIndex + 1; i < accountShells.length; i++) {
        const shell = accountShells[i];
        const key = getShellExpandKey(shell);
        if (shell.isExistingLtcPolicy) {
          if (!isExistingLtcPolicyShellComplete(ltcPolicyIds)) return key;
        } else if (
          !shell.selfFundingSource ||
          !isSelfFundingAccountComplete(shell.selfFundingSource)
        ) {
          return key;
        }
      }
      return null;
    },
    [accountShells, ltcPolicyIds],
  );

  const handleSaveComplete = useCallback(
    (shellKey: string) => {
      const nextIncomplete = getNextIncompleteShellKey(shellKey);
      setManuallyExpandedShellKey(nextIncomplete);
    },
    [getNextIncompleteShellKey],
  );

  return {
    accountShells,
    allComplete,
    expandedShellKey,
    handleToggle,
    handleSaveComplete,
  };
}
